import { useUserName } from "./hooks";
import { useAppSelector } from "@hooks/redux";
import { Divider, Popover } from "antd";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import UserAvatar from "@components/AvatarUpload/UserAvatar";
import UploadImage from "@components/UploadImage";
import { useLogout } from "@hooks/account";
import useUserInfo from "@hooks/userInfo";
import { MembershipMapStyle } from "@hooks/membership";
import { useState } from "react";
import useMemberInfo from "@hooks/useMember";
import LanguagePopover from "../LanguagePopover";
import { ThemeToggleButton } from "@components/ThemeToggleButton";
import { IconOut, IconSun, IconMoon, IconSetting } from "@icons/index";
import { themeAtom } from "@src/jotai/theme";
import { useAtomValue } from "jotai";

export const AccountDrawer = ({ isOpen }: { isOpen: boolean }) => {
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const userName = useUserName();
  const { isLogin } = useAppSelector((state) => state.globalReducer);
  const navigate = useNavigate();

  return (
    <div className="w-full">
      {isLogin ? (
        <div className="flex items-center justify-start gap-[8px]">
          <Popover
            content={<AccountDrawerContent onClose={() => setAccountDrawerOpen(false)}></AccountDrawerContent>}
            arrow={false}
            open={accountDrawerOpen}
            onOpenChange={setAccountDrawerOpen}
            trigger={"hover"}
            placement="rightTop"
          >
            <div>
              <UserAvatar className="w-[36px] h-[36px] text-[16px] relative top-[2.2px] left-[-1.8px] "></UserAvatar>
            </div>
          </Popover>
          {isOpen && <div className="flex-1">{userName || "-"}</div>}
          {isOpen && (
            <div className="cursor-pointer" onClick={() => navigate("/me")}>
              <IconSetting className="w-[20px] h-[20px]" />
            </div>
          )}
        </div>
      ) : (
        <UploadImage className="w-[36px] h-[36px] shrink-0 text-[20px]" name="f6e58ca0.png"></UploadImage>
      )}
    </div>
  );
};

const AccountDrawerContent = ({ onClose }: { onClose: () => void }) => {
  const { userInfo } = useUserInfo();
  const userName = useUserName();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const { language, isLogin, membership: _membership } = useAppSelector((state) => state.globalReducer);
  const { languageOptions } = useAppSelector((state) => state.configReducer);
  const isDark = useAtomValue(themeAtom) === "dark";

  useMemberInfo();

  const currentLanguageLabel = languageOptions.find((option) => option.value === language)?.label || "EN";

  // TODO: 暂时写死 free 版本
  // const level = memberInfo?.level || "expired";
  const level = "free";

  return (
    <div className="w-[260px] flex flex-col text-base">
      <div className="p-[8px] pb-0 flex flex-col gap-[4px]">
        <div
          className="h-[84px] rounded-[4px] flex flex-col relative cursor-pointer"
          onClick={() => {
            isLogin ? navigate("/me") : navigate("/login");
          }}
        >
          <div
            className="h-[20px] leading-[20px] px-[8px] rounded-[4px] text-[12px] font-bold absolute top-0 items-center"
            style={{
              backgroundColor: MembershipMapStyle?.[level]?.backgroundColor,
              color: MembershipMapStyle?.[level]?.textColor,
            }}
          >
            <Trans>{MembershipMapStyle?.[level]?.text}</Trans>
            {/* {level === "trial" && showDayOrHours()} */}
          </div>
          <div className="flex flex-row items-center gap-[4px] text-[12px] absolute top-0 right-0 cursor-pointer">
            <IconSetting className="w-[16px] h-[16px]" />
            <div className="relative top-px">
              <Trans>Settings</Trans>
            </div>
          </div>
          {isLogin && (
            <div className="flex flex-row gap-[4px] p-[8px] mt-[28px] z-2">
              <UserAvatar className="w-[40px] h-[40px] shrink-0 text-[20px] "></UserAvatar>
              <div className="flex flex-col justify-between text-kds-fg">
                <div className="text-[14px] line-clamp-1 font-semibold">{userName ? userName : "-"} </div>
                <div className="text-[12px] line-clamp-1">{userInfo.username}</div>
              </div>
            </div>
          )}
          {!isLogin && (
            <div className="flex flex-row gap-[4px] p-[8px] mt-[28px] items-center">
              <UploadImage className="w-[40px] h-[40px] shrink-0 text-[20px]" name="f6e58ca0.png"></UploadImage>
              <div className="flex flex-col justify-between items-center text-kds-accent">
                <Trans>Click to log in</Trans>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="pb-[8px] px-[8px]">
        <Divider style={{ margin: "4px 0" }} />
        {/* 语言部分 */}
        <LanguagePopover currentLanguageLabel={currentLanguageLabel} onClose={onClose} offset={[-12, -10]} />

        <div className="flex items-center justify-between p-[8px] kalo-tag cursor-auto!">
          <div className="flex items-center gap-1">
            {isDark ? (
              <IconMoon className="icon-mode w-[16px] h-[16px]" />
            ) : (
              <IconSun className="icon-mode w-[16px] h-[16px]" />
            )}

            <span className="text-kds-fg font-normal">
              <Trans>Theme</Trans>
            </span>
          </div>
          <ThemeToggleButton />
        </div>
        <Divider style={{ margin: "4px 0" }} />

        {isLogin && (
          <div className="flex flex-row gap-[4px] h-[38px] items-center px-[8px] kalo-tag" onClick={logout}>
            <IconOut className="w-[16px] h-[16px]" />
            <Trans>Log out</Trans>
          </div>
        )}
      </div>
    </div>
  );
};
